import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  checkRoutineDose,
  fetchRoutineCheckIns,
  fetchRoutineStreak,
  uncheckRoutineDose,
} from "~/lib/api";
import { dayKey } from "~/lib/calendar";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";
import type { RoutineCheckIn, RoutineSlot } from "~/types/routine";

/** A dose being ticked (`taken`) or unticked, on a local day key. */
export interface DoseChange {
  day: string;
  slot: RoutineSlot;
  taken: boolean;
}

function useUserId(): string {
  return useAuthStore((state) => state.user?.id) ?? "";
}

/** The doses ticked between two local days, both inclusive. */
export function useRoutineCheckIns(from: Date, to: Date) {
  const userId = useUserId();
  const fromKey = dayKey(from);
  const toKey = dayKey(to);
  return useQuery({
    queryKey: queryKeys.routineCheckIns(userId, fromKey, toKey),
    queryFn: ({ signal }) =>
      fetchRoutineCheckIns({ from: fromKey, to: toKey, signal }),
    enabled: userId.length > 0,
  });
}

/** Complete days in a row, counted back from `today`. */
export function useRoutineStreak(today: Date) {
  const userId = useUserId();
  const todayKey = dayKey(today);
  return useQuery({
    queryKey: queryKeys.routineStreak(userId, todayKey),
    queryFn: ({ signal }) => fetchRoutineStreak(todayKey, signal),
    enabled: userId.length > 0,
  });
}

function applyChange(
  items: RoutineCheckIn[],
  change: DoseChange,
): RoutineCheckIn[] {
  const others = items.filter(
    (item) => item.day !== change.day || item.slot !== change.slot,
  );
  if (!change.taken) return others;
  const existing = items.find(
    (item) => item.day === change.day && item.slot === change.slot,
  );
  return [
    ...others,
    existing ?? {
      day: change.day,
      slot: change.slot,
      created_at: new Date().toISOString(),
    },
  ];
}

/**
 * Tick or untick one dose, optimistically.
 *
 * The tile flips the moment it is pressed — the member is holding a sachet, not waiting
 * on a network — and every cached window that contains the day flips with it, so the
 * dashboard strip and the month grid never disagree. A failure puts every one of those
 * windows back and is reported by the caller. Once the server has answered either way,
 * the whole routine is re-read, which is also what moves the streak.
 */
export function useToggleDose() {
  const queryClient = useQueryClient();
  const userId = useUserId();

  return useMutation({
    mutationFn: async ({ day, slot, taken }: DoseChange): Promise<void> => {
      if (taken) await checkRoutineDose(day, slot);
      else await uncheckRoutineDose(day, slot);
    },
    onMutate: async (change) => {
      const lists = [...queryKeys.routine(userId), "check-ins"];
      await queryClient.cancelQueries({ queryKey: queryKeys.routine(userId) });
      const snapshot = queryClient.getQueriesData<RoutineCheckIn[]>({
        queryKey: lists,
      });
      for (const [key, items] of snapshot) {
        if (!items) continue;
        // `routineCheckIns` keys end in the window: [..., from, to]. Day keys are
        // zero-padded ISO dates, so string order is calendar order.
        const [from, to] = key.slice(-2) as [string, string];
        if (change.day < from || change.day > to) continue;
        queryClient.setQueryData(key, applyChange(items, change));
      }
      return { snapshot };
    },
    onError: (_error, _change, context) => {
      for (const [key, items] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, items);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.routine(userId) }),
  });
}
